/**
 * Auth helpers — Firebase Auth (Google OAuth + email/password).
 * Per design brief Al6xsFhtgGOITheXY4Ld: no Clerk. Firebase handles the
 * entire auth path. Allowlist gating on members of a workspace is a separate
 * concern (stored in Firestore, evaluated after sign-in).
 */

import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * Returns an Authorization header with a Firebase ID token for the current user.
 * Returns empty object when no user is signed in. Used to authenticate SPA calls
 * to /api/workspace/* and /api/log/search (Sprint 41 C-1/C-2 security fix).
 *
 * track:multi-workspace-membership Phase 5 — optional workspaceId adds
 * X-Workspace-Id, which server/modules/team/workspaceAuth.ts's ID-token auth
 * path (Phase 1) reads to authenticate against a non-default workspace.
 * Omitted (or "default") for backward compatibility — every existing
 * zero-arg call site keeps behaving exactly as before.
 */
export async function getAuthHeader(workspaceId?: string): Promise<Record<string, string>> {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) return {};
    const headers: Record<string, string> = { Authorization: `Bearer ${idToken}` };
    if (workspaceId && workspaceId !== "default") {
      headers["X-Workspace-Id"] = workspaceId;
    }
    return headers;
  } catch {
    return {};
  }
}

export function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider).then((cred) => cred.user);
}

export function signInWithEmail(email: string, password: string): Promise<User> {
  return signInWithEmailAndPassword(auth, email, password).then((cred) => cred.user);
}

export function signOut(): Promise<void> {
  try {
    localStorage.removeItem("relay_bearer_token");
  } catch {
    // localStorage unavailable — nothing to clean up
  }
  return fbSignOut(auth);
}

export function onUserChange(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

/**
 * relay-S2-3 — derive the workspace agent identity for the
 * authenticated viewer. Per spec #924: post author and viewer-scoped
 * subscriptions are derived from the authenticated session, never
 * hardcoded. Email local-part is the canonical mapping (e.g.
 * `travis@tber.com` → `travis`) so the resulting string lines up with
 * `active_roster` membership conventions. Falls back to `user.uid`
 * when no email is present, and `null` when there is no user.
 */
/**
 * Get workspace membership for an email address.
 * Queries relay_workspace_members by email field (supports both email-based
 * and pending_email_workspace ID patterns). Returns null if no confirmed
 * membership exists.
 */
export async function getWorkspaceMembership(
  email: string
): Promise<{ role: "lead" | "partner"; workspaceId: string } | null> {
  const normalizedEmail = email.toLowerCase();

  // First try direct lookup by email (legacy pattern from WorkspaceCreation)
  const directSnap = await getDoc(doc(db, "relay_workspace_members", normalizedEmail));
  if (directSnap.exists()) {
    const data = directSnap.data();
    const role = data.role;
    if ((role === "lead" || role === "partner" || role === "human_lead" || role === "human_partner") && data.status !== 'pending') {
      const mappedRole = (role === "human_lead" || role === "lead") ? "lead" : "partner";
      return { role: mappedRole, workspaceId: data.workspaceId ?? data.workspace_id ?? "default" };
    }
    // Direct doc exists but is still pending — we already have our answer
    // (not a confirmed member yet) from this single read. Skip the fallback
    // list query below: it filters on status=='confirmed' so it can never
    // match a pending doc anyway, and it's denied by Firestore rules for
    // non-lead callers (seq #22383) — running it here would throw for
    // legitimate pending invitees instead of just returning null.
    if (data.status === 'pending') {
      return null;
    }
  }

  // Query by email field (handles pending_${email}_${workspaceId} pattern)
  const membersRef = collection(db, 'relay_workspace_members');
  const q = query(
    membersRef,
    where('email', '==', normalizedEmail),
    where('status', '==', 'confirmed')
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  // 2026-09-02 fix (workspace-id-resolution-fix, sprint 38): this used to
  // take snapshot.docs[0] with no ordering at all. Firestore gives no
  // ordering guarantee for a query like this, so whichever confirmed
  // membership doc happened to sort first was effectively arbitrary — not
  // "most recent," not "default." An account with many confirmed
  // memberships (e.g. every workspace ever created via createWorkspace(),
  // including accidental duplicates from the workspace-creation
  // idempotency bug guarded in 7ca7539 — confirmed 133 such orphaned
  // workspace docs live) could resolve to any one of them on any given
  // session, unrelated to which workspace the account actually uses.
  //
  // Fixed with two non-arbitrary tie-breaks, in order:
  // 1. If any confirmed membership's workspace_id is literally "default",
  //    prefer it — not a hardcoded override of the RESULT, but recognizing
  //    that this codebase already treats relay_workspaces/"default" as a
  //    real, distinguished singleton doc (see createWorkspace(),
  //    server/modules/team/workspaceService.ts:32-36, which reads it
  //    directly by that literal ID as the charter-template source). If the
  //    account's own data includes a "default" membership, that IS the
  //    correct signal, not a guess.
  // 2. Otherwise (no "default" membership among the results — a genuinely
  //    different account/tenant whose canonical workspace isn't literally
  //    named "default"), pick the OLDEST by joined_at — an account's
  //    original, longest-standing membership is far more likely to be its
  //    real workspace than one created later (every one of the 133
  //    orphans was necessarily created AFTER the account's real
  //    membership). joined_at is written inconsistently across the two
  //    membership-creation paths — createWorkspace()
  //    (workspaceService.ts:81) writes an ISO string, confirmMember()
  //    (server/api/members.ts:346) writes a raw Date.now() number, and
  //    Firestore doesn't sort mixed types chronologically — so this
  //    normalizes both shapes (plus a Firestore Timestamp-like
  //    {_seconds}) in JS rather than relying on a Firestore-side orderBy.
  //    A doc with no parseable joined_at is treated as oldest (sorts
  //    first) — it's more likely a pre-joined_at-tracking legacy doc than
  //    a broken write, and this is exactly the "default" membership's
  //    likely shape (predates the field being added at all).
  const defaultDoc = snapshot.docs.find((d) => (d.data().workspace_id ?? d.data().workspaceId) === "default");
  const chosen = defaultDoc ?? snapshot.docs.reduce((oldest, d) => {
    return joinedAtMs(d.data()) < joinedAtMs(oldest.data()) ? d : oldest;
  }, snapshot.docs[0]);

  const data = chosen.data();
  const role = data.role;

  // Map backend role names to frontend role names
  if (role === 'human_lead') return { role: 'lead', workspaceId: data.workspace_id ?? "default" };
  if (role === 'human_partner') return { role: 'partner', workspaceId: data.workspace_id ?? "default" };
  if (role === 'lead' || role === 'partner') return { role, workspaceId: data.workspaceId ?? data.workspace_id ?? "default" };

  return null;
}

/** Normalizes joined_at across its known shapes (ISO string, raw ms
 * number, Firestore Timestamp-like {_seconds}) into a comparable ms
 * value. Missing/unparseable sorts as -Infinity (oldest) — see the
 * getWorkspaceMembership() comment above for why that's the safer default
 * here, not a broken-data footgun. */
function joinedAtMs(data: Record<string, unknown>): number {
  const v = data.joined_at as unknown;
  if (v == null) return -Infinity;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const ms = new Date(v).getTime();
    return isNaN(ms) ? -Infinity : ms;
  }
  if (typeof v === "object" && v !== null && "_seconds" in v) {
    const seconds = (v as { _seconds: unknown })._seconds;
    return typeof seconds === "number" ? seconds * 1000 : -Infinity;
  }
  return -Infinity;
}

/**
 * track:multi-workspace-membership Phase 2 — enumerate EVERY confirmed
 * workspace membership for a signed-in account, keyed by their real Firebase
 * Auth uid (the permanent identity anchor — unlike email, which was only
 * ever meant for the pre-signup pending-invite state and can theoretically
 * change). Deliberately does not touch getWorkspaceMembership() above,
 * which stays single-result for App.tsx's existing initial-resolution path.
 *
 * Composite-key docs (`${uid}_${workspaceId}`, written by createWorkspace()
 * and confirmMember() — the only two paths that ever create a *confirmed*
 * membership) always carry a `user_id` field, so a single `where('user_id',
 * '==', uid)` query finds every workspace this uid belongs to. The legacy
 * email-keyed doc (one per email, globally — see getWorkspaceMembership's
 * own comment) can never itself represent a SECOND membership, since it's
 * not scoped to a workspace by its own key; it's excluded here on purpose,
 * not an oversight.
 */
export async function getAllWorkspaceMemberships(
  uid: string
): Promise<Array<{ role: "lead" | "partner"; workspaceId: string }>> {
  const membersRef = collection(db, "relay_workspace_members");
  const q = query(
    membersRef,
    where("user_id", "==", uid),
    where("status", "==", "confirmed")
  );

  const snapshot = await getDocs(q);
  const seen = new Set<string>();
  const results: Array<{ role: "lead" | "partner"; workspaceId: string }> = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const workspaceId: string = data.workspace_id ?? data.workspaceId ?? "default";
    if (seen.has(workspaceId)) continue;
    seen.add(workspaceId);

    const role = data.role;
    if (role === "human_lead" || role === "lead") {
      results.push({ role: "lead", workspaceId });
    } else if (role === "human_partner" || role === "partner") {
      results.push({ role: "partner", workspaceId });
    }
    // Unrecognized role values are silently skipped, matching
    // getWorkspaceMembership()'s existing behavior above.
  }

  return results;
}

export function agentIdFromUser(user: User | null): string | null {
  if (!user) return null;
  const email = user.email?.trim().toLowerCase();
  if (email) {
    const local = email.split("@")[0];
    if (local) return local;
  }
  return user.uid || null;
}

/**
 * The signed-in human's own post author (email local part). Every human
 * member posts as themselves — never a roster lead or a hardcoded name.
 * Throws when nobody is signed in so callers' try/catch treats it like any
 * other failed action instead of misattributing the post.
 */
export function viewerAuthor(): string {
  const author = agentIdFromUser(auth.currentUser);
  if (!author) throw new Error("Sign in to post");
  return author;
}

/** Shown when the server refuses to confirm an invite for a non-Google sign-in. */
export const INVITE_GOOGLE_ONLY_MESSAGE = 'Accept this invite by signing in with Google as the invited email address.';

/** Thrown by autoConfirmPendingMembership after it has signed the person out. */
export class InviteGoogleOnlyError extends Error {
  constructor(message: string = INVITE_GOOGLE_ONLY_MESSAGE) {
    super(message);
    this.name = 'InviteGoogleOnlyError';
  }
}

/**
 * Auto-confirm pending membership on sign-in.
 *
 * Calls backend API to confirm membership using Admin SDK.
 * Returns membership data directly to avoid propagation delays.
 *
 * @param user Firebase User object
 * @returns Membership data if confirmed, null if no pending membership exists
 */
export async function autoConfirmPendingMembership(
  user: User
): Promise<{ role: "lead" | "partner"; workspaceId: string } | null> {
  if (!user.email) return null;

  try {
    const email = user.email.toLowerCase();

    // Check if pending membership exists
    const memberRef = doc(db, 'relay_workspace_members', email);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      return null; // No pending membership found
    }

    const data = memberSnap.data();
    if (data.status !== 'pending') {
      return null; // Already confirmed or invalid status
    }

    // The server resolves the caller's workspace from X-Workspace-Id (default
    // when absent), so name the workspace the person's own pending invite is
    // for, not "default", or an invite to any other workspace gets a 403.
    const inviteWorkspaceId = [data.workspace_id, data.workspaceId].find(
      (v): v is string => typeof v === 'string' && v.trim() !== ''
    )?.trim();

    // Confirm via backend API (uses Admin SDK to bypass Firestore permissions)
    const idToken = await user.getIdToken().catch(() => '');
    const response = await fetch('/api/workspace/members/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(inviteWorkspaceId ? { 'X-Workspace-Id': inviteWorkspaceId } : {}),
      },
      body: JSON.stringify({ email, uid: user.uid }),
    });

    if (response.status === 403) {
      const body = await response.clone().json().catch(() => ({}));
      if (body?.code === 'GOOGLE_SIGN_IN_REQUIRED') {
        await signOut();
        throw new InviteGoogleOnlyError(typeof body.error === 'string' ? body.error : undefined);
      }
    }

    if (!response.ok) {
      const error = await response.json();
      console.error('[auth] Failed to confirm membership:', error);
      return null;
    }

    const result = await response.json();
    console.log('[auth] Auto-confirmed pending membership for:', email);

    // Return membership data
    return {
      role: result.role as "lead" | "partner",
      workspaceId: result.workspaceId
    };
  } catch (err) {
    if (err instanceof InviteGoogleOnlyError) throw err;
    console.error('[auth] Error auto-confirming membership:', err);
    return null;
  }
}

/**
 * Mark first login for a workspace member.
 * Returns true if this was the user's first login (first_login_at was null).
 * Returns false if user has logged in before (first_login_at already exists)
 * OR if the request failed — callers should not distinguish these cases from
 * the return value alone; failures are logged clearly below.
 *
 * Used to trigger welcome messages and onboarding flows.
 *
 * track:track-mark-first-login-server-endpoint — routed through the Admin SDK
 * server endpoint. Direct client writes to relay_workspace_members are denied
 * by Firestore rules (allow update: if false), which silently broke this for
 * every login.
 */
export async function markFirstLogin(userId: string, workspaceId: string): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/first-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeader(workspaceId)) },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[markFirstLogin] Server endpoint failed:', response.status, error);
      return false;
    }

    const result = await response.json();
    if (result.isFirstLogin) {
      console.log('[markFirstLogin] First login marked for:', `${userId}_${workspaceId}`);
    }
    return Boolean(result.isFirstLogin);
  } catch (err) {
    console.error('[markFirstLogin] Request error:', err);
    return false;
  }
}
